import {
  DURATION,
  allTableColumn,
  listOfBooleanRequired,
  listOfCheckBoxFilters,
  listOfDateRequired,
  listOfFieldsNoFilter,
  listOfFieldsNotSortable,
  listOfFilterBlankRequired,
  listOfIntBooleanRequired,
  listOfNumberFilter,
} from "../../constants";
import { convertToCameCase, convertToSnakeCase, getSortedUniqueValues } from "../../utils/helpers/text";
import DATA_MODEL_TABLE from "../../utils/constants/dataModelTable";
import BooleanRender from "./BooleanRender";
import ChipRender from "./ChipRender";
import DateRender from "./DateRender";
import LinkedInstanceRender from "./LinkedInstanceRender";
import { MY_LAB, SOP_RT, TOOL_PANELS } from "../../components/DLabGrid/dLabGridConstant";
import { filter, find, keyBy } from "lodash";
import MonitoringStatusRender from "./MonitoringStatusRender";
import InstrumentAvailabilityRender from "./InstrumentAvailabilityRender";
import BookingAccessControlRender from "./BookingAccessControlRender";
import QualificationCalibrationStatusRender from "./QualificationCalibrationStatusRender";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import EquipmentStatusRender from "./EquipmentStatusRender";
import DocumentationRender from "./DocumentationRender";
import { getFieldValue } from "../booking-instruments/helpers";
import { accessControlStatus } from "./accessControlStatus";

const ACCESS_CONTROL_FILTER_VALUES = [
  accessControlStatus.allowed,
  accessControlStatus.restricted,
  accessControlStatus.trainingRequired,
  accessControlStatus.unknown,
];

const CELL_RENDERERS_BY_KEY = {
  [DATA_MODEL_TABLE.linkedInstance?.key]: LinkedInstanceRender,
  [DATA_MODEL_TABLE.tags?.key]: ChipRender,
  [DATA_MODEL_TABLE.checklists?.key]: DocumentationRender,
  [DATA_MODEL_TABLE.instrumentAvailability?.key]: InstrumentAvailabilityRender,
  [DATA_MODEL_TABLE.instrumentStatus?.key]: MonitoringStatusRender,
  [DATA_MODEL_TABLE.displayQualificationStatus?.key]: QualificationCalibrationStatusRender,
  [DATA_MODEL_TABLE.status?.key]: EquipmentStatusRender,
};

const withColumnOverrides = (column, overrides) => ({
  ...column,
  ...overrides,
});

const getStaticCellRenderer = (key) => CELL_RENDERERS_BY_KEY[key];

const getAccessControlColumn = (colObj) => {
  return withColumnOverrides(colObj, {
    cellRenderer: BookingAccessControlRender,
    filter: "agSetColumnFilter",
    filterParams: {
      buttons: ["reset"],
      defaultToNothingSelected: true,
      suppressSelectAll: true,
      values: (params) => {
        params.success(ACCESS_CONTROL_FILTER_VALUES);
      },
    },
  });
};

const getBooleanLabel = (data, key) => {
  const fieldValue = getFieldValue(data, key);
  return !!+fieldValue ? "Yes" : "No";
};

const getSpecialValueGetter = (key) => {
  if (key === DATA_MODEL_TABLE.isHazardous?.key) {
    return (params) => getBooleanLabel(params?.data, "is_hazardous");
  }

  if (key === DATA_MODEL_TABLE.shouldAssisted?.key) {
    return (params) => getBooleanLabel(params?.data, "should_assisted");
  }

  return null;
};

/** Drops empty entries, then dedupes and sorts the same way as every other set-filter column. */
const getSortedNicknames = (values = []) =>
  getSortedUniqueValues(values.filter((value) => value !== null && value !== undefined && String(value).trim() !== ""));

/**
 * Nicknames from the rows the grid currently holds.
 *
 * The grid is server-side (see EquipmentMainPage `rowModelType`), so AG Grid cannot build set
 * filter options from row data itself. These supplement the lookup master, which lags behind
 * whatever the search index has just returned.
 *
 * forEachNode is the server-side model's iterator; the fallbacks cover points in a load where it
 * yields nothing.
 */
const getLiveNicknameFilterValues = (params, key) => {
  const values = [];
  const api = params?.api;
  const snakeKey = convertToSnakeCase(key);

  const collectNickname = (node) => {
    const data = node?.data;
    // Server rows arrive snake-cased; client-side ones keep the camelCase key.
    const nickname = getFieldValue(data, key) ?? getFieldValue(data, snakeKey) ?? data?.[key] ?? data?.[snakeKey];
    if (nickname !== null && nickname !== undefined) {
      values.push(nickname);
    }
  };

  // Every scan is guarded: a throw here would escape the set filter's values callback and break the
  // popup, whereas falling through to the next source (or just the master list) keeps it usable.
  const scanSafely = (scan) => {
    try {
      scan();
    } catch (error) {
      // Fall through to the next source.
    }
  };

  scanSafely(() => api?.forEachNode?.(collectNickname));

  // Some server-side states expose displayed rows but no working node iterator.
  if (values.length === 0 && typeof api?.getDisplayedRowCount === "function") {
    scanSafely(() => {
      const displayedCount = api.getDisplayedRowCount();
      for (let index = 0; index < displayedCount; index += 1) {
        collectNickname(api.getDisplayedRowAtIndex?.(index));
      }
    });
  }

  // Last resort: rendered nodes are the rows in the viewport and always carry data once rendered,
  // including straight after a server-side sort.
  if (values.length === 0 && typeof api?.getRenderedNodes === "function") {
    scanSafely(() => (api.getRenderedNodes() || []).forEach(collectNickname));
  }

  return getSortedNicknames(values);
};

// One scrape per grid until its rows change, so reopening the filter popup costs nothing.
const liveNicknameCache = new WeakMap();

const getCachedLiveNicknameFilterValues = (params, key) => {
  const api = params?.api;
  if (typeof api?.addEventListener !== "function") {
    return getLiveNicknameFilterValues(params, key);
  }

  let entry = liveNicknameCache.get(api);
  if (!entry) {
    entry = { values: null };
    // Fires on every row-set change: block load, sort, filter, refresh.
    api.addEventListener("modelUpdated", () => {
      entry.values = null;
    });
    liveNicknameCache.set(api, entry);
  }
  if (!entry.values) {
    entry.values = getLiveNicknameFilterValues(params, key);
  }
  return entry.values;
};

const getColumnFilterOverrides = (value, masterDataFilters) => {
  if (listOfDateRequired()?.includes(value.key)) {
    return {
      cellClass: "action-render",
      cellRenderer: DateRender,
      filter: "agDateColumnFilter",
      filterParams: {
        filterOptions: ["equals", "lessThan", "greaterThan", "inRange"],
        inRangeFloatingFilterDateFormat: "DD-MMM-YYYY",
        suppressAndOrCondition: true,
        defaultJoinOperator: "OR",
        buttons: ["reset", "apply"],
      },
    };
  }

  if (listOfBooleanRequired()?.includes(value.key)) {
    return {
      cellClass: "action-render",
      cellRenderer: BooleanRender,
      filter: "agSetColumnFilter",
      filterParams: {
        defaultToNothingSelected: true,
        showTooltips: true,
        buttons: ["reset"],
        values: (params) => {
          const values = listOfIntBooleanRequired?.includes(value.key) ? ["Yes", "No"] : ["true", "false"];
          params.success(values);
        },
      },
    };
  }

  // Nickname is named explicitly rather than added to `listOfCheckBoxFilters`: that list is
  // schema-derived (see deriveGridConstants), so a legacy-list entry would stop applying the
  // moment the schema resolves and reported Nickname as a text filter.
  const isNickNameColumn = value.key === DATA_MODEL_TABLE.equipmentNickName?.key;
  if (listOfCheckBoxFilters()?.includes(value.key) || isNickNameColumn) {
    // The column describes where its own values come from, so no app-side allow-list is
    // needed: `filterValues` is present only when the schema resolved real options for it,
    // and `dynamicValuesKey` names the master-data entry to fall back to otherwise.
    const useSchemaValues = value.filterValues?.length > 0;
    let lastConfigured = null;
    let lastLive = null;
    let lastNicknames = null;
    return {
      filter: "agSetColumnFilter",
      filterParams: {
        buttons: ["reset"],
        defaultToNothingSelected: true,
        // Only Nickname draws options from loaded rows, so only it needs re-resolving on each open.
        // Other columns keep their options fixed, so a refetched master list cannot drop a tick.
        ...(isNickNameColumn && { refreshValuesOnOpen: true }),
        values: (params) => {
          if (isNickNameColumn) {
            // Nicknames are free text, so the schema has no enum for them. Take the lookup master
            // and whatever the grid has actually loaded, which covers entries the master lags on.
            const configured = masterDataFilters[value.dynamicValuesKey ?? value.key] || [];
            const live = getCachedLiveNicknameFilterValues(params, value.key);
            if (configured !== lastConfigured || live !== lastLive) {
              lastConfigured = configured;
              lastLive = live;
              lastNicknames = getSortedNicknames([...configured, ...live]);
            }
            params.success(lastNicknames);
            return;
          }

          const values =
            useSchemaValues ?
              value.filterValues :
              masterDataFilters[value.dynamicValuesKey ?? value.key] || [];
          params.success(values);
        },
      },
    };
  }

  if (listOfNumberFilter()?.includes(value.key)) {
    return {
      filter: "agNumberColumnFilter",
      filterParams: {
        filterOptions: ["equals", "lessThan", "greaterThan", "inRange"],
        defaultJoinOperator: "OR",
        buttons: ["reset", "apply"],
      },
    };
  }

  if (!listOfFieldsNoFilter().includes(value.key)) {
    return {
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: listOfFilterBlankRequired()?.includes(value.key) ? ["contains", "blank"] : ["contains"],
        buttons: ["reset", "apply"],
        defaultToNothingSelected: true,
        defaultJoinOperator: "OR",
        values: (params) => {
          const values = masterDataFilters[value.key] || [];
          params.success(values);
        },
      },
    };
  }

  return null;
};

/**
 * Restores the schema metadata a stored layout entry cannot carry.
 *
 * A saved layout holds only {key, val, order, show, sortStatus} — it is a persistence record, not
 * a column definition. A schema-derived column also carries `filter`, `filterValues`, `renderSlot`
 * and `imperialField`. Order and visibility come from what the user saved; everything else comes
 * from the schema.
 *
 * Without this, `getColumnObj` sees no `filterValues` for any *visible* column and falls back to
 * `masterDataFilters`, so a set filter renders empty as soon as a layout is saved. Only the visible
 * columns are affected, which is what makes it hard to spot. This cost instrument-repository the
 * same bug.
 *
 * @param {Object} schemaColumns - Column data keyed by column key, from `keyBy(allTableColumn(), "key")`.
 * @param {Object} storedColumn - One entry of a saved layout.
 * @returns {Object} The schema column carrying the stored order and visibility, or the stored
 *   entry unchanged when the schema no longer knows the column.
 */
export const rehydrateStoredColumn = (schemaColumns, storedColumn) =>
  schemaColumns[storedColumn.key] ?
    { ...schemaColumns[storedColumn.key], order: storedColumn.order, show: storedColumn.show } :
    storedColumn;

/**
 * Turns the grid's current columns into the persisted layout shape.
 *
 * Written from the same list the columns are read from, so a saved layout can serialise equal to
 * `DEFAULT_COLUMNS` and Reset All can disable itself. Sourcing val/show/sortStatus from
 * `DATA_MODEL_TABLE` instead would also drop every schema column that has no `DATA_MODEL_TABLE`
 * entry — they would persist as "ignore" and vanish from the layout.
 *
 * @param {Array} gridColumns - The grid's columns, each with a `colId` and `hide`.
 * @returns {{showColumns: Array, hideColumns: Array}} The layout's two halves.
 */
export const buildDisplayDefinitionFromColumns = (gridColumns = []) => {
  const schemaColumns = keyBy(allTableColumn(), "key");
  const updatedSavedColumns = gridColumns.map((column, index) => {
    const dataModelKey = convertToCameCase(column?.colId);
    const schemaColumn = schemaColumns[dataModelKey];
    return {
      key: dataModelKey,
      val: schemaColumn?.val || "ignore",
      order: index,
      // "ignore" still marks a colId that is not a data column — the actions column, for one.
      show: schemaColumn ? !column?.hide : "ignore",
      sortStatus: schemaColumn?.sortStatus ?? false,
    };
  });

  return {
    showColumns: filter(updatedSavedColumns, { show: true }),
    hideColumns: filter(updatedSavedColumns, { show: false }),
  };
};

export const getColumnObj = (value, masterDataFilters, autoGroupField = null) => {
  let colObj = {
    ...value,
    sortable: !listOfFieldsNotSortable()?.includes(value.key),
  };

  const staticCellRenderer = getStaticCellRenderer(value.key);
  if (staticCellRenderer) {
    colObj = withColumnOverrides(colObj, {
      cellRenderer: staticCellRenderer,
    });
  }

  if (autoGroupField && value.key === autoGroupField) {
    colObj = withColumnOverrides(colObj, {
      hide: true,
      lockVisible: true,
    });
  }

  if (value.key === DATA_MODEL_TABLE.bookingAccessControl?.key) {
    colObj = getAccessControlColumn(colObj);

    // Keep Access control as enum checkbox filter and avoid being overridden by fallback text filter.
    return colObj;
  }

  const specialValueGetter = getSpecialValueGetter(value.key);
  if (specialValueGetter) {
    colObj = withColumnOverrides(colObj, {
      valueGetter: specialValueGetter,
    });
  }

  const filterOverrides = getColumnFilterOverrides(value, masterDataFilters);
  if (filterOverrides) {
    colObj = withColumnOverrides(colObj, filterOverrides);
  }

  return colObj;
};

export const getColumnObjClient = (value) => {
  let colObj = value;

  colObj = {
    ...colObj,
    sortable: !listOfFieldsNotSortable()?.includes(value.key),
  };
  if (listOfBooleanRequired()?.includes(value.key)) {
    colObj = {
      ...colObj,
      cellClass: "action-render",
      cellRenderer: BooleanRender,
    };
  }
  if (value.key === DATA_MODEL_TABLE.linkedInstance?.key) {
    colObj = {
      ...colObj,
      cellRenderer: LinkedInstanceRender,
    };
  }

  if (value.key === DATA_MODEL_TABLE.tags?.key) {
    colObj = {
      ...colObj,
      cellRenderer: ChipRender,
    };
  }

  if (listOfDateRequired()?.includes(value.key)) {
    colObj = {
      ...colObj,
      cellClass: "action-render",
      cellRenderer: DateRender,
    };
  }

  return colObj;
};

//======================FILTER FUNCTIONC===================
export const onUpdateFilter = async ({
  lastFavFilter,
  isSaveConfigUpdated = false,
  updateRedux = true,
  id,
  email,
  client,
  query,
  userProfileQuery,
  updateLastFavFilter = null,
  isAdminModifyingOtherUserFilter = false,
  modifiedBy,
}) => {
  let lastFilterData = {};
  lastFilterData.lastFavFilter = lastFavFilter;
  lastFilterData = JSON.stringify(lastFilterData);
  let result = null;
  try {
    if (!isAdminModifyingOtherUserFilter) {
      result = await client.mutate({
        mutation: userProfileQuery,
        variables: { id, email, lastFilter: lastFilterData, modifiedBy },
      });
    }

    if (isSaveConfigUpdated) {
      const filterDefinition =
        typeof lastFavFilter?.filterDefinition === "string"
          ? lastFavFilter?.filterDefinition
          : JSON.stringify(lastFavFilter?.filterDefinition);
      const userFilterUpdateResult = await client.mutate({
        mutation: query,
        variables: {
          userID: id,
          filterName: lastFavFilter?.filterName,
          filterDefinition,
          modifiedBy,
        },
        fetchPolicy: "no-cache",
      });
      if (isAdminModifyingOtherUserFilter) {
        result = userFilterUpdateResult;
      }
    }

    if (isSaveConfigUpdated) {
      snackbarService.show({
        type: "success",
        message: `Updated filter successfully `,
        duration: DURATION,
      });
    }
  } catch (error) {
    if (isSaveConfigUpdated) {
      snackbarService.show({
        type: "error",
        message: "Failed to update filter",
        duration: DURATION,
      });
    }
    result = null;
  }
  if (updateRedux && updateLastFavFilter) {
    updateLastFavFilter(lastFavFilter);
  }
  return result;
};

export const onCreatefilter = async ({
  id,
  email,
  name,
  site,
  client,
  userFilters,
  lastFavFilter,
  query,
  userProfileQuery,
  updateUserFilters = null,
  updateLastFavFilter = null,
}) => {
  let result = null;
  try {
    const FilterNameMatch = userFilters.filter((item) => item.filterName === lastFavFilter?.filterName?.trim());

    if (FilterNameMatch.length === 0 && lastFavFilter?.filterName !== MY_LAB) {
      result = await client.mutate({
        mutation: query,
        variables: {
          userID: id,
          createdBy: name || email || id,
          site: site,
          filterDefinition: JSON.stringify(lastFavFilter?.filterDefinition),
          filterName: lastFavFilter.filterName?.trim(),
        },
      });
      if (result) {
        await onUpdateFilter({
          lastFavFilter,
          updateLastFavFilter,
          userProfileQuery,
          id,
          email,
          client,
        });
        if (updateUserFilters) {
          updateUserFilters([...userFilters, lastFavFilter]);
        }
      }

      snackbarService.show({
        type: "success",
        message: `Filters successfully saved `,
        duration: DURATION,
      });
    } else {
      snackbarService.show({
        type: "warning",
        message: `Duplicate filter name `,
        duration: DURATION,
      });
    }
  } catch (err) {
    console.log(err);
    snackbarService.show({
      type: "warning",
      icon: "caution",
      appName: "",
      message: `Filter creation failure `,
    });
  }

  return result;
};

//===============COLUMN FUNCTIONS=================
export const onUpdateSelectColumn = async ({
  data,
  isSaveConfigUpdated = false,
  updateLastDisplayColumnWhenEmptyData = true,
  updateRedux = true,
  id,
  email,
  name,
  client,
  userProfileQuery,
  query,
  userDisplayList,
  updateUserDisplayColumns = null,
  updateLastDisplayColumn = null,
  updateLastDisplayColumnDefinition = null,
}) => {
  let result = null;
  try {
    result = await client.mutate({
      mutation: userProfileQuery,
      variables: { id, email, lastDisplayColumns: data },
      fetchPolicy: "no-cache",
    });

    if (isSaveConfigUpdated) {
      const updateData = typeof data === "string" ? JSON.parse(data) : data;
      const displayDefinition =
        typeof updateData?.displayDefinition === "string"
          ? updateData?.displayDefinition
          : JSON.stringify(updateData?.displayDefinition);
      await client.mutate({
        mutation: query,
        variables: {
          userID: id,
          modifiedBy: name || email || id,
          displayName: updateData?.displayName,
          displayDefinition,
        },
        fetchPolicy: "no-cache",
      });
      const displayObj = typeof data === "string" ? JSON.parse(data) : data;
      userDisplayList = userDisplayList?.map((userDisplay) => {
        if (displayObj?.displayName === userDisplay?.displayName) {
          return displayObj;
        }
        return userDisplay;
      });

      if (updateUserDisplayColumns) {
        updateUserDisplayColumns([...userDisplayList]);
      }
    }

    if (isSaveConfigUpdated) {
      snackbarService.show({
        type: "success",
        message: `Updated columns set successfully `,
        duration: DURATION,
      });
    }
  } catch (error) {
    if (isSaveConfigUpdated) {
      snackbarService.show({
        type: "warning",
        message: `Failed to update columns set`,
        duration: DURATION,
      });
    }
    return null;
  }

  if (updateRedux && updateLastDisplayColumn) {
    let parsed = data;
    if (typeof data === "string") {
      try {
        parsed = JSON.parse(data);
      } catch (err) {
        parsed = null;
      }
    }

    if (parsed?.displayName || updateLastDisplayColumnWhenEmptyData) {
      updateLastDisplayColumn(data);
    } else if (parsed?.displayName === "" && updateLastDisplayColumnDefinition) {
      updateLastDisplayColumnDefinition(parsed)
    }
  }

  return result;
};

export const onCreateSelectColumn = async ({
  id,
  email,
  name,
  site,
  userDisplayList,
  unParsedData,
  client,
  query,
  userProfileQuery,
  updateUserDisplayColumns = null,
  updateLastDisplayColumn = null,
}) => {
  let result = null;
  const columnMatches = userDisplayList.filter((item) => item.displayName === unParsedData?.displayName?.trim());
  if (columnMatches?.length === 0 && unParsedData?.displayName !== "SOP RT0020") {
    let columnObj = {
      displayName: unParsedData?.displayName,
      userID: id,
      createdBy: name || email || id,
      site,
      displayDefinition: JSON.stringify(unParsedData?.displayDefinition),
    };

    try {
      result = await client.mutate({
        mutation: query,
        variables: columnObj,
        fetchPolicy: "no-cache",
      });

      if (result) {
        await onUpdateSelectColumn({
          data: JSON.stringify(unParsedData),
          updateLastDisplayColumn,
          userProfileQuery,
          id,
          email,
          name,
          client,
        });

        userDisplayList = [...userDisplayList, result?.data?.createUserDisplay];
        if (updateUserDisplayColumns) {
          updateUserDisplayColumns([...userDisplayList]);
        }
      }

      snackbarService.show({
        type: "success",
        message: `Columns Set successfully saved`,
        duration: DURATION,
      });
    } catch (error) {
      snackbarService.show({
        type: "warning",
        message: `Failed to save columns set`,
        duration: DURATION,
      });
    }
  } else {
    snackbarService.show({
      type: "warning",
      message: "Duplicate column name",
      duration: DURATION,
    });
  }
  return result;
};

//==============onChip delete======================

export const onDeleteSaveConfigurations = async ({
  userID,
  email,
  client,
  chipToDelete,
  panel,
  lastSavedChip,
  userProfileQuery,
  deleteFilterQuery,
  deleteColumnQuery,
  userFilters,
  userDisplayList,
  updateUserFilters = null,
  updateUserDisplayColumns = null,
  updateLastFavFilter = null,
  updateLastDisplayColumn = null,
  setDisplayPreset = null,
  setSavedFilter = null
}) => {
  let result = null;
  let variables = { userID: chipToDelete?.userID || userID };
  if (panel === TOOL_PANELS.filters) {
    variables = {
      ...variables,
      filterName: chipToDelete?.filterName,
    };
  } else if (panel === TOOL_PANELS.columns) {
    variables = {
      ...variables,
      displayName: chipToDelete?.displayName,
    };
  }
  try {
    result = await client.mutate({
      mutation: panel === TOOL_PANELS.filters ? deleteFilterQuery : deleteColumnQuery,
      variables,
    });
    if (panel === TOOL_PANELS.filters) {
      const removeIndex = userFilters.findIndex((item) => item.filterName === chipToDelete?.filterName);
      userFilters.splice(removeIndex, 1);
      if (lastSavedChip?.filterName === chipToDelete?.filterName) {
        await onUpdateFilter({
          lastFavFilter: {
            filterDefinition: chipToDelete?.filterDefinition,
            filterName: "",
          },
          userProfileQuery,
          updateLastFavFilter,
          id: userID,
          email,
          client,
        });
      }
      const updateFilter = [...userFilters];
      if (find(updateFilter, { filterName: MY_LAB })) {
        updateFilter.shift();
      }
      if (updateUserFilters) {
        updateUserFilters([...updateFilter]);
      }
    } else {
      if (setDisplayPreset) {
        setDisplayPreset(null)
      }
      const removeIndex = userFilters.findIndex((item) => item.displayName === chipToDelete?.displayName);
      userFilters.splice(removeIndex, 1);
      const savedColumn = typeof lastSavedChip === "string" ? JSON.parse(lastSavedChip) : lastSavedChip;
      if (savedColumn?.displayName === chipToDelete?.displayName) {
        await onUpdateSelectColumn({
          data: JSON.stringify({
            displayDefinition:
              typeof chipToDelete?.displayDefinition === "string"
                ? JSON.parse(chipToDelete?.displayDefinition)
                : chipToDelete?.displayDefinition,
            displayName: "",
          }),
          updateLastDisplayColumn,
          userProfileQuery,
          id: userID,
          email,
          client,
        });
      }
      const updateColumns = [...userFilters];
      if (find(updateColumns, { displayName: SOP_RT })) {
        updateColumns.shift();
      }
      userDisplayList = [...updateColumns];
      if (updateUserDisplayColumns) {
        updateUserDisplayColumns([...updateColumns]);
      }
    }
    snackbarService.show({
      type: "success",
      message: panel === TOOL_PANELS.filters ? "Filter successfully deleted" : "Columns set successfully deleted",
      duration: DURATION,
    });
  } catch (err) {
    snackbarService.show({
      type: "warning",
      message: panel === TOOL_PANELS.filters ? "Filter deletion failure" : "Columns set deletion failure",
      duration: DURATION,
    });
  }
  return { result, userFilters, panel };
};

export const saveLastPageSize = async ({ lastPageSize, query, updateLastPageSize, id, email, client }) => {
  try {
    await client.mutate({
      mutation: query,
      variables: { id, email, lastPageSize },
    });
    updateLastPageSize(lastPageSize);
  } catch (err) {
    console.log(" last page size fetch!", { err });
  }
};
